import { Author, Checklist, Creator } from "./Checklist";
import { Contact } from "./Contact";
import { PyramidBooking } from "./Booking";
import { Comment } from "./Comment";
import { OrderSerial } from "./OrderSerial";
import { OrderIncompleteReason } from "./OrderIncompleteReason";

export const MAX_COMMENT_LENGTH = 255;

export type Order = {
  ordernr: string;
  status: PyramidOrderStatus;
  companyName: string;
  address: string;
  postaddress: string;
  postnr: string;
  email: string | null;
  checklists: Checklist[];
  approved: boolean;
  completedBy: Creator | null;
  completedAt: string | null;
  pyramidStatus: PyramidSyncState;
  internalComments: Comment[];
  externalComments: Comment[];
  additionalExtras: Comment[];
  customerOnSite: boolean;
  customerApproval: CustomerApproval | null;
  customerRating: number | null;
  primaryContact: ContactData | null;
  secondaryContact: ContactData | null;
  approvalData: CustomerApproval | null;
  completionState: OrderStatus;
  completion: Completion | null; // Used in OrderSummary.tsx
  referenceOrder: boolean;
  customerReference: CustomerReference | null;
  customerFollowUp: boolean;
  customerApprovals: CustomerApproval[];
  companyCode: string | null;
  customerContract: string | null;
  customerOrderNumber: string | null;
  customerType: string | null;
  subCustomerType: string | null;
  averageRating: number | null;
  contractCableLabeling: boolean;
  contractSerialNumber: boolean;
  contractTheftProtection: boolean;
  contractInspection: boolean;
  contractDemonstration: boolean;
  contractSourceCode: boolean;
  contractCheatSheet: boolean;
  contractDocumentation: boolean;
  contractQrCode: boolean;
  bookings: PyramidBooking[];
  availableProducts: OrderSerial[];
  productRows: ProductRow[];
  assignedToCompany: string | null;
  additionalCompanies: { id: string, name: string, uecode: string | null }[];
  coordinator: string | null;
  selectedForReview: boolean;
  reviewedBy: Author | null;
  reviewedAt: string | null;
  // Project group fields
  parentOrderNumber: string | null;
  pairedOrderNumbers: string[];
  projectGroup: string | null;
  projectGroupName: string | null;
  projectGroupOrderNo: string | null;
  projectOrders: string[] | null;
};

export type ProductRow = {
  rowId: number;
  type: number;
  item: string;
  description: string;
  serialRequired: boolean;
  quantityOrdered: number | null;
  quantityRemaining: number | null;
  quantityDelivered: number | null;
  serials: OrderSerial[];
  // Project group fields
  orderNumber: string;
  parentRowId: number | null;
  childRows: ProductRow[];
};

export type Completion = {
  installerComment: string | null;
  completedAt: string | null;
  completedBy: Creator | null;
  completionState: OrderStatus;
  status: string;
  incompleteReason: OrderIncompleteReason | null;
};

export type CustomerApproval = {
  id: string;
  orderId: string;
  noOfChecklists: number;
  customerName: string;
  customerComment: string;
  approvedChecklists: { id: string; roomName: string }[];
  customerContactId: string | null;
  notOnSiteDescription: string | null;
  customerOnSite: boolean;
  timestamp: string;
  owner: {
    id: string;
    userName: string;
  };
  triedToContactPerson: string | null;
  triedToContactAt: string | null;
  triedToContactPhoneNumber: string | null;
};

export type CustomerReference = {
  contact: Contact;
  createdAt: Date;
  createdBy: Author;
  updatedAt: Date | null;
  updatedBy: Author | null;
};

type PyramidOrderStatus = "2" | "3" | "4" | "X";

const PyramidSyncStates = { 0: "Okänd", 1: "Ej synkad", 2: "Delvis synkad", 3: "Fel" } as const;
type PyramidSyncState = keyof typeof PyramidSyncStates;

export type OrderStatus = "UNKNOWN" | "PENDING" | "INCOMPLETE" | "COMPLETE";

export enum FilterOrderStatus {
  ALL = "ALL",
  PENDING = "PENDING",
  INCOMPLETE = "INCOMPLETE",
  COMPLETE = "COMPLETE",
  UNKNWON = "UNKNOWN",
}

export enum OrderSort {
  NONE = "NONE",
  ASC = "ASC",
  DESC = "DESC",
}

export enum SortColumn {
  NONE = "NONE",
  ORDER_NUMBER = "ORDER_NUMBER",
  COMPANY = "COMPANY",
  ORDER_STATUS = "ORDER_STATUS",
  ORDER_STATUS_UPDATED = "ORDER_STATUS_UPDATED",
  LAST_UPDATE = "LAST_UPDATE",
}

type ContactData = {
  email: string | null;
  name: string | null;
  orderId: string | null;
  phone: string | null;
};
